import {useMemo, useState} from "react";
import {ServiceEventTicketPolicyEntity} from "@ots-frontend/ots-data";
import {useDebouncedValue} from "@mantine/hooks";
import {
  DataTableFilterConfig,
  useDataTableFilter,
  UseDataTableFilterProps,
  UseDataTableFilterResult
} from "../DataTable/useDataTableFilter";
import {useTranslation} from "react-i18next";
import {getFormattedDatabaseCode} from "../../text/FormattedDatabaseCode/FormattedDatabaseCode";

export type UseServiceEventTicketPolicyDataTableFilterProps = Omit<UseDataTableFilterProps<ServiceEventTicketPolicyEntity>, 'filterConfig'>

export function useServiceEventTicketPolicyDataTableFilter(props: UseServiceEventTicketPolicyDataTableFilterProps): UseDataTableFilterResult<ServiceEventTicketPolicyEntity> {
  const {records} = props;

  const {t} = useTranslation(["common"])

  const [nameQuery, setNameQuery] = useState<string | undefined>(undefined);
  const [nameDebouncedQuery] = useDebouncedValue(nameQuery, 200);

  const [eventTypeQuery, setEventTypeQuery] = useState<string | undefined>(undefined);
  const [eventTypeDebouncedQuery] = useDebouncedValue(eventTypeQuery, 200);

  const [serviceTypeQuery, setServiceTypeQuery] = useState<string | undefined>(undefined);
  const [serviceTypeDebouncedQuery] = useDebouncedValue(serviceTypeQuery, 200);

  const [companyQuery, setCompanyQuery] = useState<string | undefined>(undefined);
  const [companyDebouncedQuery] = useDebouncedValue(companyQuery, 200);

  const filterConfig: DataTableFilterConfig<ServiceEventTicketPolicyEntity> = useMemo(() => {
    return {
      "name": {
        setQuery: setNameQuery,
        query: nameQuery,
        debouncedQuery: nameDebouncedQuery,
        filter: ({name}, query) => !name.toLowerCase().includes(query.toLowerCase())
      },
      "eventType": {
        setQuery: setEventTypeQuery,
        query: eventTypeQuery,
        debouncedQuery: eventTypeDebouncedQuery,
        filter: ({eventType}, query) => {
          if (eventType) {
            const display = getFormattedDatabaseCode(eventType, t)
            return !eventType.code.toLowerCase().includes(query.toLowerCase()) && (!display || !display.toLowerCase().includes(query.toLowerCase()))
          } else {
            return false
          }
        }
      },
      "serviceType": {
        setQuery: setNameQuery,
        query: serviceTypeQuery,
        debouncedQuery: serviceTypeDebouncedQuery,
        filter: ({serviceTypeName}, query) => !serviceTypeName || !serviceTypeName.toLowerCase().includes(query.toLowerCase())
      },
      "company": {
        setQuery: setCompanyQuery,
        query: companyQuery,
        debouncedQuery: companyDebouncedQuery,
        filter: ({companyName}, query) => !companyName || !companyName.toLowerCase().includes(query.toLowerCase())
      },
    }
  }, [
    nameQuery,
    nameDebouncedQuery,
    eventTypeQuery,
    eventTypeDebouncedQuery,
    serviceTypeQuery,
    serviceTypeDebouncedQuery,
    companyQuery,
    companyDebouncedQuery,
    t
  ])

  const {filteredRecords} = useDataTableFilter({
    records: records,
    filterConfig: filterConfig,
  })

  return {
    filteredRecords: filteredRecords,
    filterConfig: filterConfig
  };
}
